import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy, ArrowLeft, Calendar, Layers, Flag, MapPin, ChevronDown, Search, X } from "lucide-react";
import ShareButton from "@/components/ShareButton";

const LOGO_GREEN = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/4pm3nkps_Modern%20logo%20for%20%27cadii%27%20with%20golf%20theme.-2.png";
const MASCOTS_OUTLINE = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/mllzxnur_ChatGPT%20Image%2014%20abr%202026%2C%2008_14_13%20p.m..png";

const STAGE_STATUS = {
  draft: "Borrador", open: "Abierta", locked: "Cerrada",
  in_progress: "En Juego", completed: "Finalizada",
};

export default function PublicRankingPage() {
  const { slug, rankingId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("general");
  const [openStages, setOpenStages] = useState(new Set());
  const [playerSearch, setPlayerSearch] = useState("");

  const toggleStage = (stageId) => {
    setOpenStages((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) next.delete(stageId); else next.add(stageId);
      return next;
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/clubs/${slug}/rankings/${rankingId}`);
        if (!res.ok) throw new Error("No encontrado");
        setData(await res.json());
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    };
    load();
  }, [slug, rankingId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]"><div className="w-10 h-10 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F5F4F0] px-6" style={{ fontFamily: "Outfit, sans-serif" }}>
      <img src={MASCOTS_OUTLINE} alt="cadii" className="w-48 opacity-30 mb-6" />
      <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Ranking no encontrado</h1>
      <Link to={`/${slug}`} className="text-[#1B4D3E] font-medium hover:underline">Volver al club</Link>
    </div>
  );

  const { club, ranking, stages, leaderboard, total_stages_completed, live_stage, next_stage, teetimes = [], live_leaderboard = {}, completed_results = [] } = data;
  const LOGO_GREEN_LOCAL = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/4pm3nkps_Modern%20logo%20for%20%27cadii%27%20with%20golf%20theme.-2.png";
  const ogImage = club.logo_url || LOGO_GREEN_LOCAL;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareUrl = `${origin}/api/public/share/${slug}/rankings/${rankingId}`;
  const ogTitle = `${ranking.name} — ${club.name}`;
  const ogDescription = `${total_stages_completed}/${stages.length} etapas · Clasificacion general en vivo`;

  return (
    <div className="min-h-screen bg-[#F5F4F0]" style={{ fontFamily: "Outfit, sans-serif" }}>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${slug}`} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {club.logo_url ? <img src={club.logo_url} alt={club.name} className="h-6 rounded" /> : <Flag className="w-5 h-5 text-[#1B4D3E]" />}
            <span className="font-medium">{club.short_name || club.name}</span>
          </Link>
          <Link to="/" className="opacity-60 hover:opacity-100"><img src={LOGO_GREEN} alt="cadii" className="h-6 rounded-lg" /></Link>
        </div>
      </header>

      {/* Ranking Hero */}
      <section className="bg-[#1B4D3E] text-white">
        <div className="max-w-5xl mx-auto px-6 py-10 sm:py-14">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Trophy className="w-7 h-7 text-white/80" strokeWidth={1.5} />
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="public-ranking-title">{ranking.name}</h1>
              </div>
              <div className="flex flex-wrap gap-4 text-white/70 text-sm mt-2">
                {ranking.season && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {ranking.season}</span>}
                <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {total_stages_completed}/{stages.length} etapas completadas</span>
              </div>
              {ranking.categories?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {ranking.categories.map((c) => (
                    <span key={c.category_id} className="text-xs bg-white/15 text-white px-2.5 py-1 rounded-full">{c.name}</span>
                  ))}
                </div>
              )}
            </div>
            <ShareButton
              title={ogTitle}
              text={ogDescription}
              url={shareUrl}
              variant="pill"
              label="Compartir ranking"
              testId="share-ranking-btn"
            />
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Menu tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 mb-8 overflow-x-auto no-scrollbar" data-testid="ranking-menu-tabs">
          {[
            { id: "general", label: "Clasificación General" },
            { id: "resultados", label: "Resultados" },
            { id: "teetimes", label: "Tee Times" },
            ...(live_stage ? [{ id: "envivo", label: "🔴 En Vivo" }] : []),
            { id: "convocatoria", label: "Convocatoria" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#1B4D3E] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* TAB: Clasificación General */}
        {tab === "general" && (<>
        {Object.keys(leaderboard).length === 0 && (
          <p className="text-[#94A3B8] text-center py-8" data-testid="general-empty">Aún no hay puntos registrados</p>
        )}
        {/* Accumulated Leaderboard */}
        {Object.keys(leaderboard).length > 0 && (
          <div className="mb-10" data-testid="public-ranking-leaderboard">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4">Clasificacion General</h2>
            {Object.entries(leaderboard).map(([catName, entries]) => (
              <Card key={catName} className="card-default mb-4">
                <CardContent className="p-0">
                  <div className="bg-[#1B4D3E]/5 px-4 py-2.5 border-b border-gray-100">
                    <h3 className="font-bold text-sm text-[#1B4D3E]">{catName}</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-[#64748B] w-14">POS</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">JUGADOR</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-[#64748B]">ETAPAS</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-[#1B4D3E]">PUNTOS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((e, idx) => (
                          <tr key={e.member_id} className={`border-b border-gray-50 ${idx < 3 ? "bg-amber-50/30" : "hover:bg-gray-50/50"}`}>
                            <td className="text-center px-4 py-2.5">
                              {e.position <= 3 ? (
                                <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold text-white ${e.position === 1 ? "bg-amber-500" : e.position === 2 ? "bg-gray-400" : "bg-amber-700"}`}>{e.position}</span>
                              ) : (
                                <span className="font-bold text-[#0F172A]">{e.position}</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 font-medium text-[#0F172A]">{e.player_name}</td>
                            <td className="text-center px-4 py-2.5 text-[#64748B]">{e.stages_played}</td>
                            <td className="text-center px-4 py-2.5 font-bold text-[#1B4D3E] text-base">{e.total_points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stages list */}
        <h2 className="text-xl font-bold text-[#0F172A] mb-4">Etapas</h2>
        {stages.length === 0 ? (
          <p className="text-[#94A3B8]">No hay etapas creadas</p>
        ) : (
          <div className="space-y-3" data-testid="public-stages-list">
            {stages.map((s) => (
              <Link key={s.stage_id} to={`/${slug}/rankings/${rankingId}/stages/${s.stage_id}`} className="block" data-testid={`public-stage-${s.stage_id}`}>
                <Card className="card-default hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#1B4D3E]/10 flex items-center justify-center text-sm font-bold text-[#1B4D3E]">
                        {s.stage_number}
                      </div>
                      <div>
                        <p className="font-medium text-[#0F172A]">{s.name}</p>
                        <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                          {s.start_date && <span>{s.start_date}</span>}
                          <span>{s.play_days} dia{s.play_days > 1 ? "s" : ""}</span>
                          {s.point_multiplier !== 1.0 && <span className="text-amber-600 font-medium">x{s.point_multiplier}</span>}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-[#64748B] font-medium">{STAGE_STATUS[s.status] || s.status}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
        </>)}

        {/* TAB: Resultados de etapas finalizadas */}
        {tab === "resultados" && (
          <div data-testid="tab-content-resultados">
            {completed_results.length === 0 ? (
              <p className="text-[#94A3B8] text-center py-8">Aún no hay etapas finalizadas</p>
            ) : (() => {
              const q = playerSearch.trim().toLowerCase();
              const filtered = completed_results.map(({ stage: s, results }) => {
                if (!q) return { stage: s, results, matches: null };
                const fr = {};
                let matches = 0;
                Object.entries(results).forEach(([catName, rows]) => {
                  const hit = rows.filter((r) => (r.player_name || "").toLowerCase().includes(q));
                  if (hit.length) { fr[catName] = hit; matches += hit.length; }
                });
                return { stage: s, results: fr, matches };
              }).filter((x) => x.matches === null || x.matches > 0);

              return (<>
                {/* Player search */}
                <div className="relative mb-5 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <input
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    placeholder="Buscar jugador..."
                    className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#1B4D3E] transition-colors"
                    data-testid="results-player-search"
                  />
                  {playerSearch && (
                    <button onClick={() => setPlayerSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A]" data-testid="results-search-clear">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {filtered.length === 0 ? (
                  <p className="text-[#94A3B8] text-center py-8" data-testid="results-search-empty">Ningún jugador coincide con "{playerSearch}"</p>
                ) : filtered.map(({ stage: s, results, matches }) => {
                  const isOpen = q ? true : openStages.has(s.stage_id);
                  return (
                    <Card key={s.stage_id} className="card-default mb-3 overflow-hidden">
                      <button
                        onClick={() => toggleStage(s.stage_id)}
                        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50/60 transition-colors text-left"
                        data-testid={`results-stage-toggle-${s.stage_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#1B4D3E]/10 flex items-center justify-center text-sm font-bold text-[#1B4D3E] flex-shrink-0">
                            {s.stage_number}
                          </div>
                          <div>
                            <p className="font-bold text-[#0F172A] text-sm">{s.name}</p>
                            <p className="text-xs text-[#94A3B8]">
                              {s.start_date}{s.point_multiplier !== 1.0 ? ` · x${s.point_multiplier} puntos` : ""}
                              {q && matches != null ? ` · ${matches} coincidencia${matches !== 1 ? "s" : ""}` : ""}
                            </p>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-[#64748B] flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4" data-testid={`results-stage-body-${s.stage_id}`}>
                          {Object.entries(results).map(([catName, rows]) => (
                            <div key={catName} className="border border-gray-100 rounded-lg overflow-hidden mb-3 last:mb-0">
                              <div className="bg-[#1B4D3E]/5 px-4 py-2 border-b border-gray-100">
                                <h3 className="font-bold text-xs text-[#1B4D3E]">{catName}</h3>
                              </div>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-100 text-xs text-[#64748B]">
                                    <th className="text-center px-3 py-2 w-12">POS</th>
                                    <th className="text-left px-3 py-2">JUGADOR</th>
                                    <th className="text-center px-3 py-2">SCORE</th>
                                    <th className="text-center px-3 py-2 text-[#1B4D3E]">PTS</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((r) => (
                                    <tr key={r.enrollment_id} className={`border-b border-gray-50 last:border-0 ${r.status ? "opacity-60" : ""}`}>
                                      <td className="text-center px-3 py-2 font-bold">
                                        {r.status ? <span className="text-[10px] font-black bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{r.status}</span> : r.position}
                                      </td>
                                      <td className="px-3 py-2 font-medium text-[#0F172A]">{r.player_name}</td>
                                      <td className="text-center px-3 py-2 text-[#64748B]">{r.total_score ?? "-"}</td>
                                      <td className="text-center px-3 py-2 font-bold text-[#1B4D3E]">{r.points || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </>);
            })()}
          </div>
        )}

        {/* TAB: Tee Times */}
        {tab === "teetimes" && (
          <div data-testid="tab-content-teetimes">
            {(live_stage || next_stage) && (
              <p className="text-sm text-[#64748B] mb-4">
                {live_stage ? "Etapa en juego" : "Próxima etapa"}: <span className="font-bold text-[#0F172A]">{(live_stage || next_stage).name}</span>
                {(live_stage || next_stage).start_date ? ` · ${(live_stage || next_stage).start_date}` : ""}
              </p>
            )}
            {teetimes.length === 0 ? (
              <p className="text-[#94A3B8] text-center py-8">Aún no hay tee times publicados</p>
            ) : teetimes.map((tt) => (
              <div key={tt.tee_time_id} className="mb-6">
                <h3 className="font-bold text-[#0F172A] mb-3">Día {tt.day} — {(tt.groups || []).length} grupos</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(tt.groups || []).map((g) => (
                    <Card key={g.group_number} className="card-default">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-[#1B4D3E]">Grupo {g.group_number}</span>
                          <span className="text-xs font-bold text-[#0F172A]">{g.time} · Hoyo {g.hole}</span>
                        </div>
                        {(g.players || []).map((p, i) => (
                          <p key={i} className="text-sm text-[#0F172A] py-0.5">{p.name}{p.category_name ? <span className="text-xs text-[#94A3B8]"> · {p.category_name}</span> : null}</p>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TAB: En Vivo */}
        {tab === "envivo" && live_stage && (
          <div data-testid="tab-content-envivo">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              <p className="text-sm font-bold text-[#0F172A]">Etapa {live_stage.stage_number}: {live_stage.name}</p>
              <Link to={`/${slug}/rankings/${rankingId}/stages/${live_stage.stage_id}`} className="ml-auto text-xs font-semibold text-[#1B4D3E] hover:underline">Ver etapa completa →</Link>
            </div>
            {Object.keys(live_leaderboard).length === 0 ? (
              <p className="text-[#94A3B8] text-center py-8">Aún no hay scores capturados</p>
            ) : Object.entries(live_leaderboard).map(([catName, rows]) => (
              <Card key={catName} className="card-default mb-3">
                <CardContent className="p-0">
                  <div className="bg-[#1B4D3E]/5 px-4 py-2 border-b border-gray-100">
                    <h3 className="font-bold text-xs text-[#1B4D3E]">{catName}</h3>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={r.enrollment_id} className={`border-b border-gray-50 ${r.status ? "opacity-60" : ""}`}>
                          <td className="text-center px-3 py-2 w-12 font-bold">
                            {r.status ? <span className="text-[10px] font-black bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{r.status}</span> : idx + 1}
                          </td>
                          <td className="px-3 py-2 font-medium text-[#0F172A]">{r.player_name}</td>
                          <td className="text-center px-3 py-2 text-[#64748B]">{(r.rounds || []).filter(x => x != null).join(" / ") || "-"}</td>
                          <td className="text-center px-3 py-2 font-bold text-[#0F172A]">{r.total_score ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* TAB: Convocatoria */}
        {tab === "convocatoria" && (
          <div data-testid="tab-content-convocatoria">
            {ranking.convocatoria ? (
              <Card className="card-default">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-[#0F172A] mb-4">Convocatoria</h2>
                  <p className="text-sm text-[#334155] leading-relaxed whitespace-pre-wrap">{ranking.convocatoria}</p>
                </CardContent>
              </Card>
            ) : (
              <p className="text-[#94A3B8] text-center py-8">El club aún no ha publicado la convocatoria</p>
            )}
          </div>
        )}
      </div>

      <footer className="mt-12 py-8 px-6 text-center bg-white/60 border-t border-gray-100">
        <img src={LOGO_GREEN} alt="cadii" className="h-6 rounded-lg mx-auto opacity-60 mb-2" />
        <p className="text-xs text-[#9CA3AF]">&copy; {new Date().getFullYear()} cadii</p>
      </footer>
    </div>
  );
}
